import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import RoleSwitcher from '../components/RoleSwitcher';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Package, DollarSign, MapPin, Calendar, User, Phone, Award, X, Edit2, Clock, CheckCircle, XCircle, Camera } from 'lucide-react';
import { formatDateAU, formatDateShortAU } from '../utils/dateFormat';
import { ShipperQuoteNegotiation } from '../components/QuoteNegotiation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShipperDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [shipperEmail, setShipperEmail] = useState('');
  const [loads, setLoads] = useState([]);
  const [acceptedLoads, setAcceptedLoads] = useState([]);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showQuotesModal, setShowQuotesModal] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [changeLoad, setChangeLoad] = useState(null);
  const [changeData, setChangeData] = useState({});
  const [changeReason, setChangeReason] = useState('');
  const [submittingChange, setSubmittingChange] = useState(false);
  const [loadChangeRequests, setLoadChangeRequests] = useState({});
  const [historyLoads, setHistoryLoads] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoad, setEditLoad] = useState(null);
  const [editData, setEditData] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [editPhotos, setEditPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Initialize email from URL or localStorage with retry mechanism
  useEffect(() => {
    const emailFromUrl = searchParams.get('email');
    
    if (emailFromUrl) {
      // URL parameter takes priority
      setShipperEmail(emailFromUrl);
      setInitialLoading(false);
    } else {
      // Try localStorage with retry mechanism to handle race condition
      const tryGetEmail = (attempts = 0) => {
        const emailFromStorage = localStorage.getItem('user_email');
        
        if (emailFromStorage) {
          setShipperEmail(emailFromStorage);
          setInitialLoading(false);
        } else if (attempts < 5) {
          // Retry after short delay (50ms intervals, max 250ms total)
          setTimeout(() => tryGetEmail(attempts + 1), 50);
        } else {
          // No email found after retries - show authentication required
          setInitialLoading(false);
        }
      };
      
      tryGetEmail();
    }
  }, [searchParams]);

  useEffect(() => {
    if (shipperEmail) {
      fetchShipperLoads();
      fetchAcceptedLoads();
      fetchHistory();
    }
    
    // Check for payment result
    const paymentStatus = searchParams.get('payment');
    const loadId = searchParams.get('load_id');
    
    if (paymentStatus === 'success' && loadId && shipperEmail) {
      // Verify payment with backend (fallback for webhook failures)
      verifyPayment(loadId);
    } else if (paymentStatus === 'cancelled') {
      // Cancel the quote selection
      if (loadId) {
        handleCancelQuoteSelection(loadId);
      }
    }
  }, [shipperEmail]);

  // Deep-link handling: open quotes modal + highlight target quote when arriving from an email link
  const highlightQuoteId = searchParams.get('quote_id');
  const deepLinkLoadId = !searchParams.get('payment') ? searchParams.get('load_id') : null;
  useEffect(() => {
    if (!shipperEmail || !deepLinkLoadId || !highlightQuoteId) return;
    if (loads.length === 0) return; // wait for loads to arrive
    const targetLoad = loads.find(l => l.id === deepLinkLoadId);
    if (!targetLoad) return;
    // Auto-open the quotes modal for this load
    fetchQuotesForLoad(deepLinkLoadId);
  }, [shipperEmail, loads, deepLinkLoadId, highlightQuoteId]);

  // Scroll to & flash-highlight the target quote card once quotes are rendered
  useEffect(() => {
    if (!highlightQuoteId || quotes.length === 0 || !showQuotesModal) return;
    const timer = setTimeout(() => {
      const el = document.getElementById(`quote-${highlightQuoteId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('qa-highlight-flash');
        setTimeout(() => el.classList.remove('qa-highlight-flash'), 4000);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [highlightQuoteId, quotes, showQuotesModal]);

  const verifyPayment = async (loadId) => {
    try {
      const response = await axios.post(`${API}/shipper/verify-payment/${loadId}`);
      
      if (response.data.status === 'paid' || response.data.status === 'already_paid') {
        alert('Payment successful! Your load has been confirmed with the carrier. Contact details have been sent to your email.');
        // Refresh loads to show updated status
        fetchShipperLoads();
        fetchAcceptedLoads();
      } else if (response.data.status === 'unpaid') {
        alert('Payment is still processing. Please check back in a few minutes.');
      } else {
        console.log('Payment verification response:', response.data);
      }
      
      // Clear URL params
      navigate(`/shipper-dashboard?email=${shipperEmail}`, { replace: true });
    } catch (error) {
      console.error('Payment verification failed:', error);
      alert('Payment successful! Your load has been confirmed. Please refresh the page to see updated status.');
      navigate(`/shipper-dashboard?email=${shipperEmail}`, { replace: true });
    }
  };

  const fetchShipperLoads = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/shipper/loads/${shipperEmail}`);
      setLoads(response.data);
    } catch (error) {
      console.error('Failed to fetch loads:', error);
      alert('Failed to load your loads. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAcceptedLoads = async () => {
    try {
      const response = await axios.get(`${API}/shipper/accepted-loads/${shipperEmail}`);
      setAcceptedLoads(response.data);
    } catch (error) {
      console.error('Failed to fetch accepted loads:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/shipper/history/${shipperEmail}`);
      setHistoryLoads(response.data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const handleMarkCompleted = async (loadId) => {
    if (!window.confirm('Mark this load as completed? This will move it to your History.')) return;
    try {
      await axios.put(`${API}/loads/${loadId}/mark-completed`, { user_email: shipperEmail });
      alert('Load marked as completed!');
      fetchAcceptedLoads();
      fetchHistory();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to mark as completed');
    }
  };

  const handleDeleteLoad = async (loadId) => {
    if (!window.confirm('Delete this load? Any pending quotes will be cancelled. This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/shipper/loads/${loadId}?shipper_email=${shipperEmail}`);
      alert('Load deleted successfully.');
      fetchShipperLoads();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete load');
    }
  };


  const handleResumePayment = async (loadId) => {
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/shipper/resume-payment`, null, {
        params: {
          load_id: loadId,
          shipper_email: shipperEmail
        }
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Failed to resume payment:', error);
      alert(error.response?.data?.detail || 'Failed to process payment. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const fetchQuotesForLoad = async (loadId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/shipper/loads/${loadId}/quotes`);
      setQuotes(response.data);
      
      const load = loads.find(l => l.id === loadId);
      setSelectedLoad(load);
      setShowQuotesModal(true);
    } catch (error) {
      console.error('Failed to fetch quotes:', error);
      alert('Failed to load quotes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectQuote = async (quoteId) => {
    if (!selectedLoad) return;
    
    setProcessingPayment(true);
    try {
      // First select the quote
      await axios.post(`${API}/shipper/select-quote`, null, {
        params: {
          load_id: selectedLoad.id,
          quote_id: quoteId,
          shipper_email: shipperEmail
        }
      });

      // Create payment checkout
      const response = await axios.post(`${API}/shipper/payment/create-checkout`, null, {
        params: {
          load_id: selectedLoad.id,
          quote_id: quoteId,
          shipper_email: shipperEmail,
          promo_code: promoCode || undefined
        }
      });

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Failed to process payment:', error);
      alert(error.response?.data?.detail || 'Failed to process payment. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleCancelQuoteSelection = async (loadId) => {
    try {
      const load = loads.find(l => l.id === loadId);
      if (load && load.selected_quote_id) {
        await axios.post(`${API}/shipper/payment/cancel`, null, {
          params: {
            load_id: loadId,
            quote_id: load.selected_quote_id,
            shipper_email: shipperEmail
          }
        });
      }
      
      // Refresh loads
      fetchShipperLoads();
    } catch (error) {
      console.error('Failed to cancel quote selection:', error);
    }
  };

  const openChangeRequest = (load) => {
    setChangeLoad(load);
    setChangeData({
      pickup_location: load.pickup_location || '',
      dropoff_location: load.dropoff_location || '',
      cargo_type: load.cargo_type || '',
      cargo_description: load.cargo_description || '',
      weight: load.weight || '',
      dimensions: load.dimensions || '',
      pickup_date: load.pickup_date || '',
      delivery_date: load.delivery_date || '',
      special_requirements: load.special_requirements || '',
    });
    setChangeReason('');
    setShowChangeModal(true);
    fetchLoadChangeRequests(load.id);
  };

  const openEditLoad = (load) => {
    setEditLoad(load);
    setEditData({
      pickup_location: load.pickup_location || '',
      dropoff_location: load.dropoff_location || '',
      cargo_type: load.cargo_type || '',
      cargo_description: load.cargo_description || '',
      weight: load.weight || '',
      dimensions: load.dimensions || '',
      pickup_date: load.pickup_date || '',
      delivery_date: load.delivery_date || '',
      special_requirements: load.special_requirements || '',
    });
    setEditPhotos(load.load_photos || []);
    setShowEditModal(true);
  };

  const handleEditPhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { alert('File must be under 10MB'); return; }
    setUploadingPhoto(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;
        await axios.post(`${API}/shipper/loads/${editLoad.id}/photos`, {
          shipper_email: shipperEmail,
          photo_data: base64
        });
        setEditPhotos(prev => [...prev, base64]);
        setUploadingPhoto(false);
      };
      reader.readAsDataURL(file);
    } catch { setUploadingPhoto(false); alert('Upload failed'); }
  };

  const handleEditPhotoDelete = async (index) => {
    try {
      await axios.delete(`${API}/shipper/loads/${editLoad.id}/photos/${index}?shipper_email=${shipperEmail}`);
      setEditPhotos(prev => prev.filter((_, i) => i !== index));
    } catch { alert('Failed to delete photo'); }
  };

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      await axios.put(`${API}/shipper/loads/${editLoad.id}?shipper_email=${shipperEmail}`, editData);
      alert('Load updated successfully!');
      setShowEditModal(false);
      fetchShipperLoads();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update load');
    } finally { setSavingEdit(false); }
  };

  const fetchLoadChangeRequests = async (loadId) => {
    try {
      const response = await axios.get(`${API}/shipper/loads/${loadId}/change-requests?shipper_email=${shipperEmail}`);
      setLoadChangeRequests(prev => ({ ...prev, [loadId]: response.data }));
    } catch (error) {
      console.error('Failed to fetch change requests:', error);
    }
  };

  const handleSubmitChangeRequest = async () => {
    if (!changeLoad) return;
    
    // Build only the fields that actually changed
    const original = changeLoad;
    const proposed = {};
    Object.entries(changeData).forEach(([key, value]) => {
      if (String(value || '') !== String(original[key] || '')) {
        proposed[key] = value;
      }
    });
    
    if (Object.keys(proposed).length === 0) {
      alert('No changes detected. Please modify at least one field.');
      return;
    }
    
    setSubmittingChange(true);
    try {
      await axios.post(`${API}/shipper/loads/${changeLoad.id}/change-request`, {
        shipper_email: shipperEmail,
        proposed_changes: proposed,
        reason: changeReason
      });
      alert('Change request submitted! Admin will review your changes.');
      setShowChangeModal(false);
      setChangeLoad(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to submit change request.');
    } finally {
      setSubmittingChange(false);
    }
  };

  // Show loading screen while checking authentication
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 sm:px-6 py-12">
          <div className="max-w-md mx-auto">
            <Card className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a5f] mx-auto mb-4"></div>
              <p className="text-gray-600">Loading your dashboard...</p>
            </Card>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Show authentication screen only after loading is complete and no email found
  if (!shipperEmail) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <div className="container mx-auto px-4 sm:px-6 py-12">
          <div className="max-w-md mx-auto">
            <Card className="p-8 text-center">
              <h1 className="text-2xl font-bold text-[#1e3a5f] mb-4">Authentication Required</h1>
              <p className="text-gray-600 mb-6">
                Please log in to access your shipper dashboard.
              </p>
              
              <Button 
                onClick={() => navigate('/login')}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
              >
                Go to Login Page
              </Button>
            </Card>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#1e3a5f]">Shipper Dashboard</h1>
            <p className="text-gray-600 mt-2">Welcome back, {shipperEmail}</p>
          </div>
          <div className="flex gap-3">
            <RoleSwitcher />
            <Button
              onClick={() => {
                localStorage.removeItem('user_email');
                localStorage.removeItem('user_roles');
                localStorage.removeItem('active_role');
                localStorage.removeItem('user_data');
                navigate('/login');
              }}
              variant="outline"
            >
              Logout
            </Button>
          </div>
        </div>

        <Tabs defaultValue="loads" className="space-y-6">
          <TabsList>
            <TabsTrigger value="loads">My Loads</TabsTrigger>
            <TabsTrigger value="accepted">Accepted Quotes</TabsTrigger>
            <TabsTrigger value="history" data-testid="shipper-tab-history">History ({historyLoads.length})</TabsTrigger>
            <TabsTrigger value="settings">Account Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="loads" className="space-y-6">
            {loading && loads.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-gray-600">Loading your loads...</p>
              </Card>
            ) : loads.length === 0 ? (
              <Card className="p-8 text-center">
                <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Loads Posted Yet</h3>
                <p className="text-gray-600 mb-6">
                  Start by posting a load to get quotes from carriers.
                </p>
                <Button 
                  onClick={() => navigate('/request-quote')}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Post a Load
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {loads.map((load) => (
                  <Card key={load.id} className="p-6 hover:shadow-lg transition-shadow">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-[#1e3a5f]">
                          Load #{load.id.substring(0, 8)}
                        </h3>
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-2 ${
                          load.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          load.status === 'quoted' ? 'bg-blue-100 text-blue-800' :
                          load.status === 'accepted' ? 'bg-purple-100 text-purple-800' :
                          load.status === 'AWAITING_SHIPPER_PAYMENT' ? 'bg-orange-100 text-orange-800' :
                          load.status === 'AWAITING_ADHOC_PAYMENT' ? 'bg-orange-100 text-orange-800' :
                          load.status === 'paid' ? 'bg-green-100 text-green-800' :
                          load.status === 'in_transit' ? 'bg-blue-100 text-blue-800' :
                          load.status === 'delivered' ? 'bg-gray-100 text-gray-800' :
                          load.status === 'completed' ? 'bg-gray-100 text-gray-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {load.status === 'AWAITING_SHIPPER_PAYMENT' ? 'Awaiting Payment' :
                           load.status === 'AWAITING_ADHOC_PAYMENT' ? 'Carrier Payment Pending' :
                           load.status === 'paid' ? 'Paid - Ready for Transport' :
                           load.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </div>
                      
                      {load.payment_status === 'paid' && (
                        <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-1 rounded">
                          PAID
                        </span>
                      )}
                    </div>

                    <div className="space-y-3 mb-4">
                      <div className="flex items-start gap-2">
                        <MapPin className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm text-gray-600">From: <span className="font-medium text-gray-900">{load.pickup_location}</span></p>
                          <p className="text-sm text-gray-600">To: <span className="font-medium text-gray-900">{load.dropoff_location}</span></p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Package className="w-5 h-5 text-gray-500" />
                        <p className="text-sm text-gray-600">
                          {load.cargo_type} {load.weight && `- ${load.weight} kg`}
                        </p>
                      </div>

                      {load.pickup_date && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-gray-500" />
                          <p className="text-sm text-gray-600">Available From: {load.pickup_date}</p>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t gap-2">
                      <div className="flex items-center gap-2">
                        <Award className="w-5 h-5 text-blue-600" />
                        <span className="text-sm font-medium text-gray-700">
                          {load.approved_quotes_count} {load.approved_quotes_count === 1 ? 'Quote' : 'Quotes'}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        {load.approved_quotes_count === 0 && !['paid', 'delivered', 'in_transit', 'completed', 'accepted'].includes(load.status) && (
                          <Button
                            onClick={() => openEditLoad(load)}
                            variant="outline"
                            size="sm"
                            className="text-blue-700 border-blue-300 hover:bg-blue-50"
                            data-testid={`edit-load-${load.id}`}
                          >
                            <Edit2 className="w-3.5 h-3.5 mr-1" />
                            Edit
                          </Button>
                        )}
                        {load.approved_quotes_count > 0 && load.payment_status !== 'paid' && (
                          <Button
                            onClick={() => openChangeRequest(load)}
                            variant="outline"
                            size="sm"
                            className="text-amber-700 border-amber-300 hover:bg-amber-50"
                            data-testid={`request-changes-${load.id}`}
                          >
                            <Edit2 className="w-3.5 h-3.5 mr-1" />
                            Request Changes
                          </Button>
                        )}
                        {load.approved_quotes_count > 0 && load.payment_status !== 'paid' && (
                          <Button
                            onClick={() => fetchQuotesForLoad(load.id)}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            size="sm"
                          >
                            View Quotes
                          </Button>
                        )}
                        {!['paid', 'delivered', 'in_transit', 'completed', 'accepted'].includes(load.status) && (
                          <Button
                            onClick={() => handleDeleteLoad(load.id)}
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            data-testid={`delete-load-${load.id}`}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Accepted Quotes Tab */}
          <TabsContent value="accepted" className="space-y-6">
            {acceptedLoads.length === 0 ? (
              <Card className="p-8 text-center">
                <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Accepted Quotes Yet</h3>
                <p className="text-gray-600">
                  Once you accept a quote from a carrier, it will appear here.
                </p>
              </Card>
            ) : (
              <div className="space-y-4">
                {acceptedLoads.map((load) => (
                  <Card key={load.id} className={`p-6 ${load.needs_payment ? 'border-2 border-orange-300 bg-orange-50' : ''}`}>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-[#1e3a5f]">
                            Load #{load.id.substring(0, 8)}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            load.payment_completed ? 'bg-green-100 text-green-800' :
                            load.needs_payment ? 'bg-orange-100 text-orange-800' :
                            load.awaiting_adhoc_payment ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {load.payment_completed ? '✓ Paid' :
                             load.needs_payment ? 'Payment Required' :
                             load.awaiting_adhoc_payment ? 'Awaiting Carrier Fee' :
                             load.status}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-green-600" />
                            <span>{load.pickup_location}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-red-600" />
                            <span>{load.dropoff_location}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Package className="w-4 h-4 text-gray-500" />
                            <span>{load.cargo_type}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-gray-500" />
                            <span className="font-semibold">${load.quote_amount?.toLocaleString() || 'N/A'}</span>
                          </div>
                        </div>

                        {/* Carrier Contact - Only show after payment */}
                        {load.carrier_contact && (
                          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <h4 className="font-semibold text-blue-800 mb-2">🚛 Carrier Contact Details</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-blue-600" />
                                <span>{load.carrier_contact.name}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-blue-600" />
                                <a href={`tel:${load.carrier_contact.phone}`} className="text-blue-600 hover:underline">
                                  {load.carrier_contact.phone}
                                </a>
                              </div>
                              <div className="col-span-2 flex items-center gap-2">
                                <span className="text-gray-600">Email:</span>
                                <a href={`mailto:${load.carrier_contact.email}`} className="text-blue-600 hover:underline">
                                  {load.carrier_contact.email}
                                </a>
                              </div>
                              {load.carrier_contact.company_name && (
                                <div className="col-span-2 flex items-center gap-2">
                                  <span className="text-gray-600">Company:</span>
                                  <span>{load.carrier_contact.company_name}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Resume Payment Button */}
                      {load.needs_payment && (
                        <div className="flex flex-col items-end gap-2">
                          <Button
                            onClick={() => handleResumePayment(load.id)}
                            disabled={processingPayment}
                            className="bg-orange-600 hover:bg-orange-700 text-white whitespace-nowrap"
                          >
                            {processingPayment ? 'Processing...' : 'Complete Payment'}
                          </Button>
                          <p className="text-xs text-orange-700">
                            Pay to confirm this job and get carrier contact details
                          </p>
                        </div>
                      )}

                      {load.awaiting_adhoc_payment && (
                        <div className="text-right">
                          <p className="text-sm text-yellow-700 font-medium">
                            Waiting for carrier to pay their job fee
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            You'll be notified when it's time to complete payment
                          </p>
                        </div>
                      )}

                      {load.payment_completed && (load.status === 'paid' || load.status === 'in_transit') && (
                        <Button
                          onClick={() => handleMarkCompleted(load.id)}
                          variant="outline"
                          className="text-green-700 border-green-300 hover:bg-green-50"
                          data-testid={`mark-complete-${load.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> Mark as Completed
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-6">
            {historyLoads.length === 0 ? (
              <Card className="p-8 text-center">
                <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No Completed Loads</h3>
                <p className="text-gray-500">Loads you mark as completed will appear here for your records.</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {historyLoads.map(load => (
                  <Card key={load.id} className="p-5" data-testid={`history-load-${load.id}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-bold text-[#1e3a5f]">{load.pickup_location} → {load.dropoff_location}</h3>
                        <p className="text-sm text-gray-500">{load.cargo_type}</p>
                      </div>
                      <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                        load.status === 'delivered' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {load.status === 'delivered' ? 'Completed' : load.status.toUpperCase()}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-gray-600">
                      {load.quote_amount && (
                        <div><span className="font-medium">Quote:</span> ${load.quote_amount.toLocaleString()}</div>
                      )}
                      {load.pickup_date && (
                        <div><span className="font-medium">Available From:</span> {formatDateAU(load.pickup_date)}</div>
                      )}
                      {load.completed_at && (
                        <div><span className="font-medium">Completed:</span> {formatDateAU(load.completed_at)}</div>
                      )}
                      {load.created_at && (
                        <div><span className="font-medium">Posted:</span> {formatDateAU(load.created_at)}</div>
                      )}
                    </div>
                    {load.carrier_contact && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
                        <p className="font-medium text-gray-700 mb-1">Carrier:</p>
                        <p>{load.carrier_contact.name} {load.carrier_contact.company_name ? `— ${load.carrier_contact.company_name}` : ''}</p>
                        <p className="text-gray-500">{load.carrier_contact.email} | {load.carrier_contact.phone}</p>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="settings" className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">Account Information</h2>
              <div className="space-y-4">
                <div>
                  <Label>Email Address</Label>
                  <Input value={shipperEmail} disabled className="bg-gray-100" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">
                    To update your account information, please contact support.
                  </p>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Quotes Modal */}
      {showQuotesModal && selectedLoad && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-[#1e3a5f]">
                  Quotes for Load #{selectedLoad.id.substring(0, 8)}
                </h2>
                <p className="text-gray-600 mt-1">
                  {selectedLoad.pickup_location} → {selectedLoad.dropoff_location}
                </p>
              </div>
              <button
                onClick={() => setShowQuotesModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {quotes.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-600">No quotes available for this load yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {quotes.map((quote) => (
                    <Card
                      key={quote.id}
                      id={`quote-${quote.id}`}
                      data-testid={`quote-card-${quote.id}`}
                      className={`p-6 border-2 hover:border-blue-300 transition-all duration-500 ${
                        highlightQuoteId === quote.id ? 'ring-2 ring-yellow-400 shadow-lg' : ''
                      }`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-[#1e3a5f]">
                            {quote.carrier_name || 'Carrier'}
                          </h3>
                          {quote.carrier_company && (
                            <p className="text-sm text-gray-600">{quote.carrier_company}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-red-600">
                            ${((quote.final_amount || quote.amount) + 50).toLocaleString()}
                          </p>
                          <p className="text-xs text-gray-500">Carrier: ${(quote.final_amount || quote.amount).toLocaleString()} + $50 fee</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-600">
                            {quote.estimated_delivery_days} days delivery
                          </span>
                        </div>
                        {quote.carrier_phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-600">{quote.carrier_phone}</span>
                          </div>
                        )}
                      </div>

                      {quote.message && (
                        <div className="bg-gray-50 p-4 rounded mb-4">
                          <p className="text-sm text-gray-700">{quote.message}</p>
                        </div>
                      )}

                      <div className="border-t pt-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Input
                            type="text"
                            placeholder="Enter promo code"
                            value={promoCode}
                            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                            className="flex-1"
                          />
                        </div>
                        
                        {/* Payment Breakdown */}
                        <div className="bg-gray-50 p-3 rounded mb-3 text-sm">
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-600">Carrier quote:</span>
                            <span>${(quote.final_amount || quote.amount).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-600">Service fee:</span>
                            <span>$50.00</span>
                          </div>
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-600">Card processing fee (1.7% + $0.30):</span>
                            <span>${(((quote.final_amount || quote.amount) + 50) * 0.017 + 0.30).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between font-semibold border-t pt-1 mt-1">
                            <span>Total:</span>
                            <span>${((quote.final_amount || quote.amount) + 50 + ((quote.final_amount || quote.amount) + 50) * 0.017 + 0.30).toFixed(2)}</span>
                          </div>
                        </div>
                        
                        <Button
                          onClick={() => handleSelectQuote(quote.id)}
                          disabled={processingPayment}
                          className="w-full bg-red-600 hover:bg-red-700 text-white"
                          data-testid="select-pay-quote-btn"
                        >
                          {processingPayment ? 'Processing...' : 'Select & Pay by Card'}
                        </Button>
                        
                        <div className="mt-3 p-4 bg-green-50 rounded-lg border-2 border-green-300">
                          <p className="text-sm font-bold text-green-800 mb-2">Pay by Bank Transfer — Save on Card Fees!</p>
                          <div className="bg-white rounded p-3 text-sm space-y-1.5">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Account Name:</span>
                              <span className="font-bold text-gray-900">Truckn Send It</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">BSB:</span>
                              <span className="font-bold text-gray-900">065-175</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Account:</span>
                              <span className="font-bold text-gray-900">1053 5947</span>
                            </div>
                            <div className="flex justify-between border-t pt-1.5 mt-1.5">
                              <span className="text-gray-600">Reference:</span>
                              <span className="font-bold text-red-600">{selectedLoad?.id?.substring(0, 8) || 'Load ID'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Amount (no card fee):</span>
                              <span className="font-bold text-green-700">${((quote.final_amount || quote.amount) + 50).toFixed(2)}</span>
                            </div>
                          </div>
                          <p className="text-xs text-green-700 mt-2">
                            Transfer the above amount and use the Load ID as your payment reference. 
                            Contact <a href="mailto:info@trucknsendit.com" className="underline font-semibold">info@trucknsendit.com</a> or 
                            call <a href="tel:1300380700" className="underline font-semibold">1300 380 700</a> to confirm.
                          </p>
                        </div>
                      </div>

                      {/* Negotiation thread + Negotiate button */}
                      <ShipperQuoteNegotiation
                        quoteId={quote.id}
                        shipperEmail={shipperEmail}
                        highlightNegotiationId={searchParams.get('negotiation_id')}
                      />
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      

      {/* Edit Load Modal (for loads with 0 quotes) */}
      {showEditModal && editLoad && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 my-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-[#1e3a5f]">Edit Load</h2>
              <button onClick={() => setShowEditModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Pickup Location</Label>
                  <Input value={editData.pickup_location} onChange={(e) => setEditData(prev => ({ ...prev, pickup_location: e.target.value }))} />
                </div>
                <div>
                  <Label>Dropoff Location</Label>
                  <Input value={editData.dropoff_location} onChange={(e) => setEditData(prev => ({ ...prev, dropoff_location: e.target.value }))} />
                </div>
                <div>
                  <Label>Cargo Type</Label>
                  <Input value={editData.cargo_type} onChange={(e) => setEditData(prev => ({ ...prev, cargo_type: e.target.value }))} />
                </div>
                <div>
                  <Label>Weight</Label>
                  <Input value={editData.weight} onChange={(e) => setEditData(prev => ({ ...prev, weight: e.target.value }))} />
                </div>
                <div>
                  <Label>Dimensions</Label>
                  <Input value={editData.dimensions} onChange={(e) => setEditData(prev => ({ ...prev, dimensions: e.target.value }))} />
                </div>
                <div>
                  <Label>Available From</Label>
                  <Input type="date" value={editData.pickup_date} onChange={(e) => setEditData(prev => ({ ...prev, pickup_date: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label>Cargo Description</Label>
                <textarea className="w-full border rounded-md p-2 text-sm" rows={2} value={editData.cargo_description} onChange={(e) => setEditData(prev => ({ ...prev, cargo_description: e.target.value }))} />
              </div>
              <div>
                <Label>Special Requirements</Label>
                <textarea className="w-full border rounded-md p-2 text-sm" rows={2} value={editData.special_requirements} onChange={(e) => setEditData(prev => ({ ...prev, special_requirements: e.target.value }))} />
              </div>

              {/* Photos */}
              <div className="border-t pt-4">
                <Label className="text-sm font-semibold">Freight Photos</Label>
                <p className="text-xs text-gray-500 mb-3">Upload photos of your freight to help carriers provide accurate quotes.</p>
                
                {editPhotos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {editPhotos.map((photo, i) => (
                      <div key={i} className="relative group">
                        <img src={photo} alt={`Photo ${i + 1}`} className="w-full h-24 object-cover rounded-lg border" />
                        <button
                          onClick={() => handleEditPhotoDelete(i)}
                          className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                        >&times;</button>
                      </div>
                    ))}
                  </div>
                )}
                
                <label className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-sm text-gray-600">
                  <Camera className="w-4 h-4" />
                  {uploadingPhoto ? 'Uploading...' : 'Add Photo'}
                  <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={handleEditPhotoUpload} className="hidden" disabled={uploadingPhoto} />
                </label>
              </div>

              <div className="flex gap-3 pt-2">
                <Button onClick={handleSaveEdit} disabled={savingEdit} className="flex-1 bg-[#1e3a5f] hover:bg-[#15304f] text-white" data-testid="save-edit-btn">
                  {savingEdit ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="outline" onClick={() => setShowEditModal(false)} className="flex-1">Cancel</Button>
              </div>
            </div>
          </Card>
        </div>
      )}


      {/* Change Request Modal */}
      {showChangeModal && changeLoad && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 my-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-[#1e3a5f]">Request Changes to Load</h2>
              <Button variant="outline" size="sm" onClick={() => setShowChangeModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
              Since quotes have been submitted on this load, changes must be approved by admin before appearing on the live listing.
            </p>

            {/* Show existing pending requests */}
            {loadChangeRequests[changeLoad.id]?.some(r => r.status === 'pending') && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-yellow-800 flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  You have a pending change request for this load. Please wait for admin review.
                </p>
              </div>
            )}

            {/* Previous requests history */}
            {loadChangeRequests[changeLoad.id]?.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Previous Requests</p>
                {loadChangeRequests[changeLoad.id].slice(0, 3).map((req) => (
                  <div key={req.id} className="flex items-center gap-2 text-sm mb-1">
                    {req.status === 'approved' && <CheckCircle className="w-4 h-4 text-green-600" />}
                    {req.status === 'rejected' && <XCircle className="w-4 h-4 text-red-600" />}
                    {req.status === 'pending' && <Clock className="w-4 h-4 text-yellow-600" />}
                    <span className="capitalize">{req.status}</span>
                    <span className="text-gray-400">—</span>
                    <span className="text-gray-500">{new Date(req.created_at).toLocaleDateString()}</span>
                    {req.rejection_reason && <span className="text-red-600 text-xs">({req.rejection_reason})</span>}
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Pickup Location</Label>
                  <Input
                    value={changeData.pickup_location || ''}
                    onChange={(e) => setChangeData(prev => ({ ...prev, pickup_location: e.target.value }))}
                    data-testid="change-pickup-location"
                  />
                </div>
                <div>
                  <Label>Dropoff Location</Label>
                  <Input
                    value={changeData.dropoff_location || ''}
                    onChange={(e) => setChangeData(prev => ({ ...prev, dropoff_location: e.target.value }))}
                    data-testid="change-dropoff-location"
                  />
                </div>
                <div>
                  <Label>Cargo Type</Label>
                  <Input
                    value={changeData.cargo_type || ''}
                    onChange={(e) => setChangeData(prev => ({ ...prev, cargo_type: e.target.value }))}
                    data-testid="change-cargo-type"
                  />
                </div>
                <div>
                  <Label>Weight</Label>
                  <Input
                    value={changeData.weight || ''}
                    onChange={(e) => setChangeData(prev => ({ ...prev, weight: e.target.value }))}
                    data-testid="change-weight"
                  />
                </div>
                <div>
                  <Label>Dimensions</Label>
                  <Input
                    value={changeData.dimensions || ''}
                    onChange={(e) => setChangeData(prev => ({ ...prev, dimensions: e.target.value }))}
                    data-testid="change-dimensions"
                  />
                </div>
                <div>
                  <Label>Available From</Label>
                  <Input
                    type="date"
                    value={changeData.pickup_date || ''}
                    onChange={(e) => setChangeData(prev => ({ ...prev, pickup_date: e.target.value }))}
                    data-testid="change-pickup-date"
                  />
                </div>
                <div>
                  <Label>Delivery Date</Label>
                  <Input
                    type="date"
                    value={changeData.delivery_date || ''}
                    onChange={(e) => setChangeData(prev => ({ ...prev, delivery_date: e.target.value }))}
                    data-testid="change-delivery-date"
                  />
                </div>
              </div>
              
              <div>
                <Label>Cargo Description</Label>
                <Textarea
                  value={changeData.cargo_description || ''}
                  onChange={(e) => setChangeData(prev => ({ ...prev, cargo_description: e.target.value }))}
                  rows={2}
                  data-testid="change-cargo-description"
                />
              </div>
              
              <div>
                <Label>Special Requirements</Label>
                <Textarea
                  value={changeData.special_requirements || ''}
                  onChange={(e) => setChangeData(prev => ({ ...prev, special_requirements: e.target.value }))}
                  rows={2}
                  data-testid="change-special-requirements"
                />
              </div>

              <div>
                <Label>Reason for Changes</Label>
                <Textarea
                  value={changeReason}
                  onChange={(e) => setChangeReason(e.target.value)}
                  placeholder="Briefly explain why you need these changes..."
                  rows={2}
                  data-testid="change-reason"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4 mt-4 border-t">
              <Button
                onClick={handleSubmitChangeRequest}
                disabled={submittingChange || loadChangeRequests[changeLoad.id]?.some(r => r.status === 'pending')}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="submit-change-request-btn"
              >
                {submittingChange ? 'Submitting...' : 'Submit Change Request'}
              </Button>
              <Button variant="outline" onClick={() => setShowChangeModal(false)}>
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default ShipperDashboard;
